import React from 'react';
import { DraggableModal } from '@ui/shared/components/Common/DraggableModal';
import { Tabs } from '@smarterservices/smarter-ui';
import { getStore, useSettingsStore } from '../../../stores/StoreContext';
import { useFullScreenMountNode } from '../../../contexts/FullScreenContext';
import AudioSettings from './AudioSettings';
import GeneralSettings from './GeneralSettings';

/**
 * SettingsModal Component
 * 
 * Draggable modal containing application settings organized into tabs.
 * Currently includes Audio and General settings tabs. Modal state is managed
 * by the SettingsStore and can be opened from various parts of the application.
 * 
 * Features:
 * - Draggable and resizable modal
 * - Tabbed interface for organizing settings
 * - Full screen mode support via FullScreenContext
 * - Persistent tab state
 * 
 * @returns {JSX.Element} Settings modal with tabbed interface
 * 
 * @example
 * // Open from anywhere in the app
 * const SettingsStore = getStore('settingsStore');
 * SettingsStore.openSettingsModal('audio'); // Opens to Audio tab
 */
function SettingsModal() {
    const SettingsStore = getStore('settingsStore');
    const isOpen = useSettingsStore((state) => state.isSettingsModalOpen);
    const activeTab = useSettingsStore((state) => state.activeTab);
    const mountNode = useFullScreenMountNode();

    /**
     * Handle modal close
     */
    const handleClose = () => {
        SettingsStore.closeSettingsModal();
    };

    /**
     * Handle tab change
     * 
     * @param {Event} event - Tab change event
     * @param {Object} params - Event parameters
     * @param {number} params.index - Index of selected tab
     */
    const handleTabChange = (event, { index }) => {
        const tabIds = ['general', 'audio'];
        SettingsStore.setActiveTab(tabIds[index]);
    };

    return (
        <DraggableModal
            isOpen={isOpen}
            onClose={handleClose}
            title="Settings"
            initialSize={{ width: 600, height: 500 }}
            resizable={true}
            minimizable={true}
            minSize={{ width: 500, height: 400 }}
            maxSize={{ width: 800, height: 800 }}
            constrainToContainer={false}
            id="settings-modal"
            mountNode={mountNode}
        >
            <Tabs onRequestTabChange={handleTabChange}>
                {/* 
                <Tabs.Panel renderTitle="General" id="general-tab" isSelected={activeTab === 'general'}>
                    <GeneralSettings />
                </Tabs.Panel>
                */}
                <Tabs.Panel renderTitle="Audio" id="audio-tab" isSelected={activeTab === 'audio'}>
                    <AudioSettings />
                </Tabs.Panel>
            </Tabs>
        </DraggableModal>
    );
}

export default SettingsModal;
